import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
    WorkflowCustomerOption,
    StartWorkflowRequest,
    WorkflowAvailableAction,
    WorkflowDefinition,
    WorkflowHistory,
    WorkflowInstance,
    WorkflowRoleOption,
    WorkflowResourceType,
    WorkflowStatusNodeLayout,
    WorkflowStatusNode,
    WorkflowTransition,
    WorkflowTransitionCondition,
    WorkflowTransitionRequest
} from './workflow.model';

@Injectable({ providedIn: 'root' })
export class WorkflowService {
    private readonly baseUrl = `${environment.restApiUrl}workflow`;

    constructor(private readonly http: HttpClient) { }

    getResourceTypes(companyId: number) {
        return this.http.get<WorkflowResourceType[]>(`${this.baseUrl}/resource-types`, {
            params: new HttpParams().set('companyId', companyId)
        });
    }

    saveResourceType(resourceType: WorkflowResourceType) {
        return this.http.post<WorkflowResourceType>(`${this.baseUrl}/resource-types`, resourceType);
    }

    getDefinitions(companyId: number, resourceKey?: string) {
        let params = new HttpParams().set('companyId', companyId);
        if (resourceKey) {
            params = params.set('resourceKey', resourceKey);
        }
        return this.http.get<WorkflowDefinition[]>(`${this.baseUrl}/definitions`, { params });
    }

    getRoleOptions(companyId?: number | null, companyGroupId?: number | null) {
        let params = new HttpParams();
        if (companyId) {
            params = params.set('companyId', companyId);
        }
        if (companyGroupId) {
            params = params.set('companyGroupId', companyGroupId);
        }
        return this.http.get<WorkflowRoleOption[]>(`${this.baseUrl}/role-options`, { params });
    }

    getCustomerOptions(companyId?: number | null, companyGroupId?: number | null) {
        let params = new HttpParams();
        if (companyId) {
            params = params.set('companyId', companyId);
        }
        if (companyGroupId) {
            params = params.set('companyGroupId', companyGroupId);
        }
        return this.http.get<WorkflowCustomerOption[]>(`${this.baseUrl}/customer-options`, { params });
    }

    saveDefinition(definition: WorkflowDefinition) {
        return this.http.post<WorkflowDefinition>(`${this.baseUrl}/definitions`, definition);
    }

    updateDefinition(definition: WorkflowDefinition) {
        return this.http.put<WorkflowDefinition>(`${this.baseUrl}/definitions/${definition.workflowDefinitionId}`, definition);
    }

    saveNode(workflowDefinitionId: number, node: WorkflowStatusNode) {
        return this.http.post<WorkflowStatusNode>(`${this.baseUrl}/definitions/${workflowDefinitionId}/nodes`, node);
    }

    saveNodeLayout(workflowDefinitionId: number, layout: WorkflowStatusNodeLayout[]) {
        return this.http.put<WorkflowStatusNode[]>(`${this.baseUrl}/definitions/${workflowDefinitionId}/nodes/layout`, layout);
    }

    deleteNode(workflowDefinitionId: number, nodeId: number) {
        return this.http.delete<void>(`${this.baseUrl}/definitions/${workflowDefinitionId}/nodes/${nodeId}`);
    }

    saveTransition(workflowDefinitionId: number, transition: WorkflowTransition) {
        return this.http.post<WorkflowTransition>(`${this.baseUrl}/definitions/${workflowDefinitionId}/transitions`, transition);
    }

    deleteTransition(transitionId: number) {
        return this.http.delete<void>(`${this.baseUrl}/transitions/${transitionId}`);
    }

    saveCondition(transitionId: number, condition: WorkflowTransitionCondition) {
        return this.http.post<WorkflowTransitionCondition>(`${this.baseUrl}/transitions/${transitionId}/conditions`, condition);
    }

    deleteCondition(conditionId: number) {
        return this.http.delete<void>(`${this.baseUrl}/conditions/${conditionId}`);
    }

    startWorkflow(request: StartWorkflowRequest) {
        return this.http.post<WorkflowInstance>(`${this.baseUrl}/instances/start`, request);
    }

    getInstance(companyId: number, resourceKey: string, resourceId: number) {
        return this.http.get<WorkflowInstance>(`${this.baseUrl}/instances/${resourceKey}/${resourceId}`, {
            params: new HttpParams().set('companyId', companyId)
        });
    }

    getAvailableActions(instanceId: number, performedByCustomerId?: number | null, context: Record<string, unknown> = {}) {
        let params = new HttpParams();
        if (performedByCustomerId) {
            params = params.set('performedByCustomerId', performedByCustomerId);
        }
        if (Object.keys(context).length) {
            params = params.set('contextJson', JSON.stringify(context));
        }
        return this.http.get<WorkflowAvailableAction[]>(`${this.baseUrl}/instances/${instanceId}/available-actions`, { params });
    }

    transition(instanceId: number, request: WorkflowTransitionRequest) {
        return this.http.post<WorkflowInstance>(`${this.baseUrl}/instances/${instanceId}/transition`, request);
    }

    getHistory(instanceId: number) {
        return this.http.get<WorkflowHistory[]>(`${this.baseUrl}/instances/${instanceId}/history`);
    }
}
